"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { ArrowRight, CircleCheck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const features = [{
  title: "Professional & Reliable Team",
  description: "Our staff are rigorously vetted, trained, and dedicated to maintaining the highest standards of professionalism in every commercial environment."
}, {
  title: "Attention to Detail",
  description: "We don't just clean; we care. From high-touch surfaces to hidden corners, our meticulous approach ensures a spotless workspace every time."
}, {
  title: "Flexible Cleaning Schedules",
  description: "We work around your business hours to minimise disruption. Whether you need early morning, late night, or weekend cleaning, we adapt to you."
}, {
  title: "Fully Insured & Trusted",
  description: "Peace of mind is part of the service. We are fully insured and trusted by leading businesses across Newcastle, Central Coast, and Sydney."
}];
export default function WhyChooseGoldStar() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="why-choose-gold-star" ref={ref} className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
          {}
          <div className="lg:col-span-5 lg:sticky lg:top-32 h-fit space-y-8">
            <div className={`transition-all duration-700 delay-100 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <h2 className="text-primary font-medium tracking-wider uppercase text-sm mb-4">
                Why Choose Us
              </h2>
              <h3 className="text-4xl md:text-5xl font-serif font-light leading-tight text-white mb-6">
                The Gold Standard in <span className="text-primary italic">Commercial Cleaning</span>
              </h3>
              <p className="text-muted-foreground text-lg leading-relaxed max-w-md">We elevate commercial cleaning from a commodity to a premium service. Experience the difference of a partner who values your business' image as much as you do.</p>
            </div>
            
            <div className={`transition-all duration-700 delay-200 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8">
                <Link to="/ContactUsPage">
                  Get a Free Quote <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 relative">
            {}
            <div className="absolute left-[27px] md:left-[39px] top-4 bottom-0 w-px bg-gradient-to-b from-primary/50 via-border to-transparent hidden md:block" />

            <div className="space-y-16 md:space-y-24">
              {features.map((feature, index) => <div key={index} data-index={index} className={`relative flex flex-col md:flex-row gap-6 md:gap-12 group transition-all duration-700 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
              transitionDelay: `${300 + index * 150}ms`
            }}>
                  {}
                  <div className="flex-shrink-0 relative">
                    <div className="flex items-center justify-center w-14 h-14 md:w-20 md:h-20 rounded-full bg-background border border-border group-hover:border-primary/50 transition-colors duration-500 relative z-10">
                      <span className="font-serif text-2xl md:text-3xl text-primary">
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>
                  </div>

                  {}
                  <div className="pt-2 md:pt-4 border-l border-border/30 md:border-none pl-6 md:pl-0 ml-7 md:ml-0">
                    <h4 className="text-2xl font-medium text-white mb-3 group-hover:text-primary transition-colors duration-300">
                      {feature.title}
                    </h4>
                    <p className="text-muted-foreground leading-relaxed text-lg">
                      {feature.description}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </div>
    </section>;
}